import React, { useState } from 'react';
import { Zap, Loader, Play } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { RootState } from '../../../reducers';
import logger from '../../../lib/logger';
import { fetchSubscription } from '../../plan/thunk';
import { setAlertMessage } from '../dashboardSlice';
import { endTrialImmediately } from '../../../services/subscriptionServices';

const ActivateNowButton: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { plan, team } = useSelector((state: RootState) => state);
  const { teamId } = team.selectedTeam || {};
  const {
    state: subscriptionState,
    provider,
    price,
    term,
  } = plan.subscription || {};

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Only show for Stripe trial subscriptions
  const isTrialing = subscriptionState === 'trialing' && provider === 'stripe';

  if (!isTrialing) {
    return null;
  }

  // Calculate the price to display
  const priceDisplay = price ? `$${price}` : '';
  let termDisplay = '';
  if (term === 'monthly') {
    termDisplay = 'month';
  } else if (term === 'annual') {
    termDisplay = 'year';
  }

  const toggleVideo = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsVideoPlaying(true);
      } else {
        videoRef.current.pause();
        setIsVideoPlaying(false);
      }
    }
  };

  const handleActivate = async () => {
    if (!teamId) {
      logger.error('No teamId found');
      return;
    }

    setIsActivating(true);
    setError(null);

    try {
      const subscriptionData = await endTrialImmediately({ teamId });

      if (!subscriptionData) {
        throw new Error('Failed to activate subscription');
      }

      // Refresh subscription data
      await dispatch(fetchSubscription({ teamId }));

      // Close dialog
      setIsDialogOpen(false);

      // Show success message
      dispatch(
        setAlertMessage({
          type: 'success',
          message: t(
            'billing.trial.activateSuccess',
            'Your subscription has been activated! You are now on the paid plan.',
          ),
        }),
      );

      logger.info('Trial activated successfully', { teamId });
    } catch (err: any) {
      const errorMessage =
        err?.message ||
        t(
          'billing.trial.activateError',
          'Failed to activate subscription. Please try again.',
        );
      logger.error('Failed to activate trial', { error: err, teamId });
      setError(errorMessage);

      dispatch(
        setAlertMessage({
          type: 'error',
          message: errorMessage,
        }),
      );
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <>
      <Button
        variant="default"
        size="default"
        onClick={() => setIsDialogOpen(true)}
        className="h-10 px-3"
      >
        <Zap className="mr-1 h-4 w-4" />
        {t('billing.trial.activateMyPlan', 'Activate My Plan')}
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader className="space-y-4">
            <DialogTitle className="text-xl font-semibold">
              {t('billing.trial.stayActivated', 'Ready to activate?')}
            </DialogTitle>
            <DialogDescription className="text-base">
              {priceDisplay && termDisplay
                ? t(
                    'billing.trial.priceDescription',
                    `${priceDisplay}/${termDisplay}, billed today. Cancel anytime.`,
                  )
                : t(
                    'billing.trial.billedToday',
                    'Billed today. Cancel anytime.',
                  )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 pt-2">
            {/* Video Preview */}
            <div
              role="button"
              tabIndex={0}
              className="relative group rounded-lg overflow-hidden bg-muted/50 cursor-pointer"
              onClick={toggleVideo}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleVideo();
                }
              }}
            >
              <video
                ref={videoRef}
                className="w-full rounded-lg"
                onEnded={() => setIsVideoPlaying(false)}
              >
                <source
                  src="https://screenapp.io/screenapp-resources/upgrade-view/videos/v1/1minutedemo.mp4"
                  type="video/mp4"
                />
                <track kind="captions" src="" label="English" srcLang="en" />
              </video>
              {!isVideoPlaying && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center hover:scale-110 transition-transform">
                    <Play className="h-6 w-6 text-primary-foreground ml-0.5" />
                  </div>
                </div>
              )}
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Action Buttons */}
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsDialogOpen(false)}
                disabled={isActivating}
              >
                {t('billing.trial.keepTrial', 'Keep trial for now')}
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={handleActivate}
                disabled={isActivating}
              >
                {isActivating ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    {t('billing.trial.activating', 'Activating...')}
                  </>
                ) : (
                  t('billing.trial.activateNowButton', 'Activate Now')
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ActivateNowButton;
